import React, { useMemo, useState } from 'react';
import { Scale, ChevronRight, ChevronDown, Users, CircleDot } from 'lucide-react';
import PropertyCard from './ui/PropertyCard';
import DataItem from './ui/DataItem';
import Alert from '@mui/material/Alert';

/**
 * Componente para visualização de dados jurídicos relacionados à propriedade
 * @param {Object} props - Propriedades do componente
 * @param {Object} props.propertyData - Dados da propriedade
 * @param {Function} props.onHighlightGeometry - Função para destacar geometria no mapa
 * @returns {JSX.Element} Componente de visualização de dados jurídicos
 */
const LegalTab = ({ propertyData }) => {
  const [expandedDetails, setExpandedDetails] = useState({});
  const activeStatusAliases = new Set(['ATIVO', 'EM ANDAMENTO']);

  const normalizeStatusText = (value) =>
    String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toUpperCase();

  const isActiveStatus = (value) => activeStatusAliases.has(normalizeStatusText(value));

  const resolveProcessStatus = (processo) => {
    const rawStatus = processo?.status;
    if (!rawStatus) return '';
    if (!Array.isArray(rawStatus) && typeof rawStatus !== 'object') return String(rawStatus);

    const statusList = Array.isArray(rawStatus) ? rawStatus : [rawStatus];
    for (const statusItem of statusList) {
      const resolved =
        statusItem?.tribunal || statusItem?.normalizado || statusItem?.inferido || '';
      if (resolved) return resolved;
    }
    return '';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '--';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR');
    } catch (_error) {
      return dateString;
    }
  };

  const toggleDetails = (entityId) => {
    setExpandedDetails((prev) => ({
      ...prev,
      [entityId]: !prev[entityId],
    }));
  };

  const parsed = useMemo(() => {
    const props = propertyData?.properties || {};
    const legalKey = Object.keys(props).find((k) => k.toLowerCase() === 'legal');
    const legalData = legalKey ? props[legalKey] : null;
    const list = Array.isArray(legalData) ? legalData : legalData ? [legalData] : [];

    const byDocument = new Map();

    const toArray = (value) => {
      if (Array.isArray(value)) return value;
      if (value && typeof value === 'object') return [value];
      return [];
    };

    const getDocInfo = (entry, fallbackType = 'DESCONHECIDO') => {
      const identificacaoRaw = entry?.identificacao;
      const identificacao = Array.isArray(identificacaoRaw)
        ? identificacaoRaw[0] || {}
        : identificacaoRaw || {};

      const tipo =
        entry?.document_type ||
        identificacao?.tipo ||
        (entry?.cpf ? 'CPF' : entry?.cnpj ? 'CNPJ' : fallbackType);

      const valor = entry?.document_id || identificacao?.valor || entry?.cpf || entry?.cnpj || '--';

      return {
        tipo: String(tipo || fallbackType).toUpperCase(),
        valor,
      };
    };

    for (const item of list) {
      const civilEntries = toArray(item?.civilstatus);
      const criminalEntries = toArray(item?.criminalstatus);
      const mergedEntries = [
        ...civilEntries.map((entry) => ({ entry, source: 'civil' })),
        ...criminalEntries.map((entry) => ({ entry, source: 'criminal' })),
      ];

      if (mergedEntries.length === 0) {
        const docInfo = getDocInfo(item, 'DESCONHECIDO');
        const key = `${docInfo.tipo}|${String(docInfo.valor)}`;
        if (!byDocument.has(key)) {
          byDocument.set(key, {
            id: key,
            documento: docInfo.valor,
            tipo: docInfo.tipo,
            nome: 'Nome não disponível',
            dataConsulta: item?.data_consulta || null,
            civil: [],
            criminal: [],
            ativos: [],
          });
        }
      }

      for (const { entry, source } of mergedEntries) {
        const docInfo = getDocInfo(entry, item?.document_type || 'DESCONHECIDO');
        const key = `${docInfo.tipo}|${String(docInfo.valor)}`;
        const processos = toArray(entry?.processos);
        const ativos = processos.filter((p) => isActiveStatus(resolveProcessStatus(p)));

        if (!byDocument.has(key)) {
          byDocument.set(key, {
            id: key,
            documento: docInfo.valor,
            tipo: docInfo.tipo,
            nome: entry?.nome || 'Nome não disponível',
            dataConsulta: item?.data_consulta || null,
            civil: [],
            criminal: [],
            ativos: [],
          });
        }

        const row = byDocument.get(key);
        if (source === 'civil') row.civil.push(...processos);
        if (source === 'criminal') row.criminal.push(...processos);
        row.ativos.push(...ativos);
        if (!row.nome || row.nome === 'Nome não disponível') {
          row.nome = entry?.nome || row.nome;
        }
        if (!row.dataConsulta && item?.data_consulta) row.dataConsulta = item.data_consulta;
      }
    }

    const documentos = Array.from(byDocument.values()).map((row) => ({
      ...row,
      civil: dedupeProcesses(row.civil),
      criminal: dedupeProcesses(row.criminal),
      ativos: dedupeProcesses(row.ativos),
    }));

    const totalProcessosCivis = documentos.reduce((acc, cur) => acc + cur.civil.length, 0);
    const totalProcessosCriminais = documentos.reduce((acc, cur) => acc + cur.criminal.length, 0);

    return {
      documentos,
      totalProcessosCivis,
      totalProcessosCriminais,
      totalDocumentos: documentos.length,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyData]);

  const formatCPF = (cpf) => {
    if (!cpf) return '--';
    cpf = String(cpf).replace(/\D/g, '');
    if (cpf.length !== 11) return cpf;
    return cpf.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
  };

  const formatCNPJ = (cnpj) => {
    if (!cnpj) return '--';
    cnpj = String(cnpj).replace(/\D/g, '');
    if (cnpj.length !== 14) return cnpj;
    return cnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  };

  const formatDocument = (doc, tipo) => {
    if (!doc) return '--';
    if (tipo === 'CPF') return formatCPF(doc);
    if (tipo === 'CNPJ') return formatCNPJ(doc);
    return doc;
  };

  const renderProcessList = (processos, title) => {
    if (!Array.isArray(processos) || processos.length === 0) {
      return <div className="text-gray-500 text-sm">Sem processos.</div>;
    }

    return (
      <div className="space-y-2">
        <div className="text-sm font-medium text-gray-700">
          {title} ({processos.length})
        </div>
        {processos.map((processo, idx) => (
          <div key={`${title}-${idx}`} className="p-3 border border-gray-200 rounded-md bg-gray-50">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <DataItem label="Número" value={processo.numero_processo || '--'} />
              <DataItem label="Tipo" value={processo.tipo_processo || '--'} />
              <DataItem label="Natureza" value={processo.natureza || '--'} />
              <DataItem label="Assunto" value={processo.assunto || '--'} />
              <DataItem label="Status" value={resolveProcessStatus(processo) || '--'} />
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderDocumento = (documento) => {
    const isExpanded = expandedDetails[documento.id] || false;

    return (
      <div key={documento.id} className="mb-4 border border-gray-200 rounded-lg">
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-t-lg">
          <div className="flex items-center">
            <CircleDot size={16} className="text-indigo-600 mr-2" />
            <h4 className="font-medium">{documento.nome}</h4>
          </div>
          <div className="text-sm text-gray-500">
            {formatDocument(documento.documento, documento.tipo)}
          </div>
        </div>

        <div className="p-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <DataItem label="Tipo documento" value={documento.tipo} />
            <DataItem label="Processos cíveis" value={String(documento.civil.length)} />
            <DataItem label="Processos criminais" value={String(documento.criminal.length)} />
            <DataItem label="Processos ativos" value={String(documento.ativos.length)} />
            <DataItem label="Data da consulta" value={formatDate(documento.dataConsulta)} />
          </div>

          <div className="mt-3">
            <button
              onClick={() => toggleDetails(documento.id)}
              className="flex items-center text-indigo-600 font-medium hover:text-indigo-800"
            >
              {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
              <span>Detalhes do documento</span>
            </button>

            {isExpanded && (
              <div className="mt-3 space-y-3">
                {renderProcessList(documento.civil, 'Processos cíveis')}
                {renderProcessList(documento.criminal, 'Processos criminais')}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const hasLegalData = parsed.totalDocumentos > 0;

  return (
    <div className="space-y-4">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <Scale className="text-indigo-600 mr-2" size={20} />
          <h3 className="text-lg font-medium text-indigo-800">Informações Jurídicas</h3>
        </div>
      </div>

      {/* Resumo Geral */}
      <PropertyCard
        title="Visão Consolidada"
        icon={<Scale className="text-indigo-600" size={18} />}
      >
        {!hasLegalData ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            Não há informações jurídicas disponíveis para esta propriedade.
          </Alert>
        ) : parsed.totalProcessosCivis === 0 && parsed.totalProcessosCriminais === 0 ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            Esta propriedade foi avaliada, mas não foram encontrados processos jurídicos ativos.
          </Alert>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <DataItem
              label="Total de Processos Cíveis"
              value={parsed.totalProcessosCivis.toString()}
            />
            <DataItem
              label="Total de Processos Criminais"
              value={parsed.totalProcessosCriminais.toString()}
            />
            <DataItem label="Documentos avaliados" value={parsed.totalDocumentos.toString()} />
          </div>
        )}
      </PropertyCard>

      {parsed.documentos.length > 0 && (
        <PropertyCard title="Documentos" icon={<Users className="text-indigo-600" size={18} />}>
          <div className="space-y-2">
            {parsed.documentos.map((documento) => renderDocumento(documento))}
          </div>
        </PropertyCard>
      )}
    </div>
  );
};

function dedupeProcesses(list) {
  const arr = Array.isArray(list) ? list : [];
  const map = new Map();
  for (const item of arr) {
    const key = [item?.numero_processo, item?.tipo_processo, item?.assunto]
      .map((v) =>
        String(v || '')
          .trim()
          .toLowerCase(),
      )
      .join('|');
    if (!map.has(key)) map.set(key, item);
  }
  return Array.from(map.values());
}

export default LegalTab;
